import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IFormDialogProps } from '~/components/molecules/FormDialog';
import type { IApiGetAppResponseBody } from '~/lib/api/modules/Apps';
import type {
  IWPLeaderboardFormProps,
  IWPLeaderboardFormValues,
} from './WPLeaderboardForm';
import { FormDialog } from '~/components/molecules/FormDialog';
import { useActivateWPLeaderboardMutation } from '~/lib/api/modules/WPLeaderboard/hooks/useActivateWPLeaderboardMutation';
import { WPLeaderboardForm } from './WPLeaderboardForm';

export type IWPLeaderboardFormDialogProps = Pick<
  IFormDialogProps,
  'opened' | 'onClose'
> &
  IWPLeaderboardFormProps & {
    appId: string;
  };

export const WPLeaderboardFormDialog: React.FC<
  IWPLeaderboardFormDialogProps
> = (props) => {
  const { opened, onClose, appId, ...other } = props;
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const activateWPLeaderboardMutation = useActivateWPLeaderboardMutation();

  const handleClose = (): void => {
    setIsSubmitting(false);
    onClose?.();
  };

  const handleSubmit = async (
    values: IWPLeaderboardFormValues,
  ): Promise<IApiGetAppResponseBody> => {
    setIsSubmitting(true);

    return activateWPLeaderboardMutation
      .mutateAsync({
        appId,
        body: {
          iapPollId: values.iapPollId!,
          defaultPictureId: values.defaultPictures![0].id,
        },
      })
      .finally(handleClose);
  };

  return (
    <FormDialog
      opened={opened}
      onClose={handleClose}
      headline={t('WPLeaderboardFormDialog.headline')}
      submitButtonLabel={t('common.activate')}
      size="md"
      submitting={isSubmitting}
    >
      {({ formId }) => (
        <WPLeaderboardForm
          id={formId}
          extended
          hideActionBar
          onSubmit={handleSubmit}
          onFailure={() => setIsSubmitting(false)}
          appId={appId}
          {...other}
        />
      )}
    </FormDialog>
  );
};
